import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import api from '../lib/api';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const checkAuthInProgress = React.useRef(false);

  // Check if user is authenticated via our backend
  const checkAuth = useCallback(async () => {
    if (checkAuthInProgress.current) return;
    checkAuthInProgress.current = true;
    try {
      const response = await api.get('/api/auth/me');

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return false;
      }
      console.error('Auth check failed:', err);
      setUser(null);
      return false;
    } finally {
      checkAuthInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Check backend session on mount
    const initAuth = async () => {
      await checkAuth();
      if (mounted) setLoading(false);
    };

    initAuth();

    // Also listen for Supabase auth changes (for OAuth)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user && mounted) {
          // OAuth login detected, sync with backend
          await checkAuth();
        }
        if (mounted) setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [checkAuth]);

  // Login with email and password
  const loginWithEmail = async (email, password) => {
    setError(null);
    try {
      const response = await api.post('/api/auth/login', { email, password });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Login failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Network error. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Register with email and password
  const registerWithEmail = async (email, password, name) => {
    setError(null);
    try {
      const response = await api.post('/api/auth/register', { email, password, name });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Registration failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Network error. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login with Google OAuth (via Supabase)
  const loginWithGoogle = async () => {
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        setError(error.message);
        console.error('Error logging in with Google:', error.message);
      }
    } catch (err) {
      setError('Failed to initiate Google login');
    }
  };

  // Request password reset
  const requestPasswordReset = async (email) => {
    setError(null);
    try {
      const response = await api.post('/api/auth/password-reset-request', { email });
      const data = await response.json();
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: 'Failed to send reset email' };
    }
  };

  // Confirm password reset
  const confirmPasswordReset = async (token, newPassword) => {
    setError(null);
    try {
      const response = await api.post('/api/auth/password-reset-confirm', {
        token,
        new_password: newPassword,
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, message: data.message };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Password reset failed' };
      }
    } catch (err) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Logout
  const logout = async () => {
    try {
      // Logout from backend
      await api.post('/api/auth/logout', {});

      // Also logout from Supabase (for OAuth sessions)
      await supabase.auth.signOut();

      setUser(null);
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      // Force logout on frontend even if backend fails
      setUser(null);
      window.location.href = '/';
    }
  };

  // Legacy login function for backward compatibility
  const login = loginWithGoogle;

  const value = {
    user,
    loading,
    error,
    // Email/Password auth
    loginWithEmail,
    registerWithEmail,
    requestPasswordReset,
    confirmPasswordReset,
    // OAuth
    loginWithGoogle,
    // Common
    logout,
    checkAuth,
    // Legacy
    login,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
